import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getNotifications, markAllNotificationsRead, markNotificationRead } from '../api/notifications';

function formatTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const difference = Date.now() - date.getTime();
  const hours = Math.floor(difference / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function NotificationBell({ enabled }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await getNotifications();
      setItems(Array.isArray(data.notifications) ? data.notifications : []);
      setUnread(Number(data.unread || 0));
      setError('');
    } catch {
      setError('Notifications are temporarily unavailable.');
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const close = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const openNotification = async (item) => {
    if (!item.read_at) {
      setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, read_at: new Date().toISOString() } : entry));
      setUnread((count) => Math.max(0, count - 1));
      try { await markNotificationRead(item.id); } catch { load(); }
    }
    setOpen(false);
  };

  const readAll = async () => {
    setItems((current) => current.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() })));
    setUnread(0);
    try { await markAllNotificationsRead(); } catch { load(); }
  };

  if (!enabled) return null;

  return (
    <div className="notificationBellRoot" ref={rootRef}>
      <button className="bell" type="button" aria-label={`${unread} unread notifications`} aria-expanded={open} onClick={() => setOpen((value) => !value)} onMouseEnter={(event) => { event.currentTarget.style.backgroundColor = '#e0e0e0'; }} onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0)'; }}>
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="21" height="21" viewBox="0 0 48 48" aria-hidden="true">
          <path d="M 23.277344 4.0175781 C 15.193866 4.3983176 9 11.343391 9 19.380859 L 9 26.648438 L 6.3496094 31.980469 A 1.50015 1.50015 0 0 0 6.3359375 32.009766 C 5.2696804 34.277268 6.9957076 37 9.5019531 37 L 18 37 C 18 40.295865 20.704135 43 24 43 C 27.295865 43 30 40.295865 30 37 L 38.496094 37 C 41.002339 37 42.730582 34.277829 41.664062 32.009766 A 1.50015 1.50015 0 0 0 41.650391 31.980469 L 39 26.648438 L 39 19 C 39 10.493798 31.863289 3.6133643 23.277344 4.0175781 z M 23.417969 7.0136719 C 30.338024 6.6878857 36 12.162202 36 19 L 36 27 A 1.50015 1.50015 0 0 0 36.15625 27.667969 L 38.949219 33.289062 C 39.128826 33.674017 38.921017 34 38.496094 34 L 9.5019531 34 C 9.077027 34 8.8709034 33.674574 9.0507812 33.289062 C 9.0507812 33.289062 9.0507812 33.287109 9.0507812 33.287109 L 11.84375 27.667969 A 1.50015 1.50015 0 0 0 12 27 L 12 19.380859 C 12 12.880328 16.979446 7.3169324 23.417969 7.0136719 z M 21 37 L 27 37 C 27 38.674135 25.674135 40 24 40 C 22.325865 40 21 38.674135 21 37 z" />
        </svg>
        {unread > 0 && <span className="notificationBadge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notificationPanel" role="dialog" aria-label="Notifications">
          <div className="notificationPanelHeader">
            <strong>Notifications</strong>
            {unread > 0 && <button type="button" onClick={readAll}>Mark all read</button>}
          </div>
          <div className="notificationList">
            {error && <p className="notificationEmpty">{error}</p>}
            {!error && items.length === 0 && <p className="notificationEmpty">No earnings notifications yet.</p>}
            {items.map((item) => (
              <Link key={item.id} to={`/stock/${encodeURIComponent(item.ticker)}`} className={`notificationItem ${item.read_at ? '' : 'isUnread'}`} onClick={() => openNotification(item)}>
                <span className="notificationDot" />
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.body}</small>
                  <time>{formatTime(item.created_at)}</time>
                </span>
              </Link>
            ))}
          </div>
          <Link className="notificationSettingsLink" to="/Account" onClick={() => setOpen(false)}>Email preferences</Link>
        </div>
      )}
    </div>
  );
}
